import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, Route, Routes, useNavigate, useParams } from "react-router-dom";

import { ApiError } from "../../api/client";
import { useMe } from "../../auth/useMe";
import { HierarchyApi } from "../../admin/hierarchy/api";
import { useScopeNames } from "../../admin/hierarchy/useScopeNames";
import { useUsersById } from "../../admin/rbac/useUsersById";
import { useMarkSeen } from "../../notifications/useMarkSeen";
import { Wizard, WizardField, WizardSection, ScopePicker, type WizardStep } from "../../wizard/Wizard";
import { ThreatStepBody, type ThreatTo } from "../../wizard/ThreatStep";
import { ResolutionPanel } from "../../shell/ResolutionPanel";
import { AssignAsTaskButton } from "../../shell/AssignAsTaskButton";
import { CasesApi, type Case, type CaseStatus, type Classification } from "./api";

export function CasesRoutes() {
  return (
    <Routes>
      <Route index element={<CasesList />} />
      <Route path="new" element={<NewCase />} />
      <Route path=":id" element={<CaseDetail />} />
    </Routes>
  );
}

function CasesList() {
  const [status, setStatus] = useState<CaseStatus | "">("");
  const [classification, setClassification] = useState<Classification | "">("");
  const [search, setSearch] = useState("");
  const cases = useQuery({
    queryKey: ["cases", status],
    queryFn: () => CasesApi.list(status ? { status } : undefined),
  });
  useMarkSeen("cases", cases.dataUpdatedAt);

  // Newest first; client-side classification + search filter on top of the
  // server's status filter.
  const items = (cases.data?.items ?? [])
    .filter((c) => !classification || c.classification === classification)
    .filter((c) => !search || (c.title + " " + (c.summary ?? "")).toLowerCase().includes(search.toLowerCase()))
    .slice()
    .sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""));

  return (
    <div>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 4 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Cases</h1>
          <p className="muted" style={{ margin: 0, fontSize: 14 }}>Open a new case to record an investigation, track its progress, and close it when done.</p>
        </div>
        <Link to="new"><button>New case</button></Link>
      </header>

      <div className="row-3" style={{ marginTop: 16 }}>
        <label>
          Status:
          <select value={status} onChange={(e) => setStatus(e.target.value as CaseStatus | "")}>
            <option value="">All</option>
            <option value="open">Open</option>
            <option value="in_review">In review</option>
            <option value="closed">Closed</option>
          </select>
        </label>
        <label>
          Classification:
          <select value={classification} onChange={(e) => setClassification(e.target.value as Classification | "")}>
            <option value="">All</option>
            <option value="unclassified">Unclassified</option>
            <option value="restricted">Restricted</option>
            <option value="secret">Secret</option>
          </select>
        </label>
        <label>
          Search:
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Title or summary…" />
        </label>
      </div>

      {cases.isLoading && <p>Loading…</p>}
      {cases.error && <p className="error">Failed to load: {(cases.error as Error).message}</p>}

      <div className="card-grid">
        {items.map((c) => (
          <Link key={c.id} to={c.id} className="list-card">
            <div className="list-card-header">
              <ClassificationBadge level={c.classification} />
              <StatusBadge status={c.status} />
            </div>
            <div className="list-card-title">{c.title}</div>
            <div className="list-card-snippet">{c.summary || "No summary."}</div>
            <div className="list-card-foot">
              <span>{c.subjectName || "No subject"}</span>
              <span>{c.createdAt ? new Date(c.createdAt).toLocaleDateString() : "—"}</span>
            </div>
          </Link>
        ))}
        {items.length === 0 && !cases.isLoading && (
          <div className="muted" style={{ padding: 16, gridColumn: "1 / -1" }}>No cases match your filters.</div>
        )}
      </div>
    </div>
  );
}

function CaseDetail() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { isSuperadmin, permissions } = useMe();
  const canUpdate = isSuperadmin || permissions.includes("case.update");
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const c = useQuery({ queryKey: ["case", id], queryFn: () => CasesApi.get(id), enabled: !!id });
  const { name: userName } = useUsersById();
  const { branchName, wardName, constituencyName } = useScopeNames();

  const close = useMutation({
    mutationFn: () => CasesApi.update(id, { status: "closed" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["case", id] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : "Failed"),
  });
  const reopen = useMutation({
    mutationFn: () => CasesApi.update(id, { status: "open" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["case", id] }),
  });
  const remove = useMutation({
    mutationFn: () => CasesApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cases"] });
      nav("..", { replace: true });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Failed"),
  });
  const save = useMutation({
    mutationFn: (patch: Partial<{ title: string; summary: string; classification: Classification; subjectName: string; subjectIdNumber: string; subjectPhone: string }>) =>
      CasesApi.update(id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["case", id] });
      qc.invalidateQueries({ queryKey: ["cases"] });
      setEditing(false);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Failed"),
  });

  if (c.isLoading) return <p>Loading…</p>;
  if (c.error) return <p className="error">{(c.error as Error).message}</p>;
  if (!c.data) return null;

  return (
    <div className="hierarchy">
      <header>
        <Link to=".." className="muted">← All cases</Link>
        <h1 style={{ marginTop: 4 }}>
          {c.data.title}{" "}
          <ClassificationBadge level={c.data.classification} />
          <StatusBadge status={c.data.status} />
        </h1>
      </header>
      {error && <div className="error">{error}</div>}

      {editing ? (
        <CaseEditForm
          initial={c.data}
          onCancel={() => setEditing(false)}
          onSave={(patch) => {
            if (window.confirm("Save changes to this case? The audit log keeps the before/after.")) {
              save.mutate(patch);
            }
          }}
          saving={save.isPending}
        />
      ) : (
        <>
          <div className="card">
            <h3 className="detail-section-title">Case</h3>
            <dl className="kv">
              <dt>Summary</dt><dd style={{ whiteSpace: "pre-wrap" }}>{c.data.summary || "—"}</dd>
              <dt>Classification</dt><dd>{c.data.classification}</dd>
              <dt>Status</dt><dd>{c.data.status.replace("_", " ")}</dd>
            </dl>
          </div>

          <div className="card detail-section">
            <h3 className="detail-section-title">Where</h3>
            <dl className="kv">
              <dt>Constituency</dt><dd>{constituencyName(c.data.constituencyId)}</dd>
              <dt>Ward</dt><dd>{wardName(c.data.wardId)}</dd>
              <dt>Branch</dt><dd>{branchName(c.data.branchId)}</dd>
            </dl>
          </div>

          <div className="card detail-section">
            <h3 className="detail-section-title">Subject (PII — masked unless reader has pii.read)</h3>
            <dl className="kv">
              <dt>Name</dt><dd>{c.data.subjectName || "—"}</dd>
              <dt>National ID</dt><dd>{c.data.subjectIdNumber || "—"}</dd>
              <dt>Phone</dt><dd>{c.data.subjectPhone || "—"}</dd>
            </dl>
          </div>

          <div className="card detail-section">
            <h3 className="detail-section-title">Audit</h3>
            <dl className="kv">
              <dt>Created by</dt><dd>{userName(c.data.createdBy)}</dd>
              <dt>Created</dt><dd>{c.data.createdAt ? new Date(c.data.createdAt).toLocaleString() : "—"}</dd>
            </dl>
          </div>

          <ResolutionPanel
            collection="cases"
            recordId={c.data.id}
            current={c.data.resolutionStatus}
            onResolved={() => qc.invalidateQueries({ queryKey: ["case", id] })}
          />

          <div style={{ marginTop: 12 }}>
            <AssignAsTaskButton
              relatedType="case"
              relatedId={c.data.id}
              suggestedTitle={`Action case: ${c.data.title}`}
            />
          </div>
        </>
      )}

      <div className="detail-actions">
        {canUpdate && !editing && (
          <button className="ghost" onClick={() => setEditing(true)}>Edit details</button>
        )}
        {c.data.status === "closed" ? (
          <button
            className="ghost"
            onClick={() => {
              if (window.confirm("Reopen this case? It will return to active status.")) reopen.mutate();
            }}
          >Reopen</button>
        ) : (
          <button
            onClick={() => {
              if (window.confirm("Close this case? You can reopen it later if needed. The full history is kept either way.")) close.mutate();
            }}
          >Close</button>
        )}
        {/* Delete intentionally removed — operational records are append-only.
            Use Close to deactivate a case; the audit log keeps the trail. */}
      </div>
    </div>
  );
}

function NewCase() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const scope = useQuery({ queryKey: ["my-scope"], queryFn: HierarchyApi.myScope });
  const [cid, setCid] = useState("");
  const [wid, setWid] = useState("");
  const [bid, setBid] = useState("");
  const constituencies = scope.data?.constituencies ?? [];
  const wardsForCid = (scope.data?.wards ?? []).filter((w) => w.constituencyId === cid);
  const branchesForWid = (scope.data?.branches ?? []).filter((b) => b.wardId === wid);

  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [classification, setClassification] = useState<Classification>("unclassified");
  const [subjectName, setSubjectName] = useState("");
  const [subjectIdNumber, setSubjectIdNumber] = useState("");
  const [subjectPhone, setSubjectPhone] = useState("");
  const [threatTo, setThreatTo] = useState<ThreatTo>("none");
  const [threatTarget, setThreatTarget] = useState("");
  const [threatDetails, setThreatDetails] = useState("");

  const create = useMutation({
    mutationFn: CasesApi.create,
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ["cases"] });
      nav(`../${c.id}`, { replace: true });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Failed"),
  });

  function submit() {
    if (!title || !bid) return;
    create.mutate({
      title,
      summary,
      classification,
      branchId: bid,
      subjectName: subjectName || undefined,
      subjectIdNumber: subjectIdNumber || undefined,
      subjectPhone: subjectPhone || undefined,
      threatTo,
      threatTarget: threatTarget || undefined,
      threatDetails: threatDetails || undefined,
    });
  }

  const steps: WizardStep[] = [
    {
      title: "Where",
      canNext: !!bid,
      blockerHint: "Pick a constituency, ward and branch",
      body: (
        <WizardSection title="Where does this case belong?" hint="Cases attach to a single branch in your scope.">
          <ScopePicker
            constituencies={constituencies}
            wards={wardsForCid}
            branches={branchesForWid}
            cid={cid} wid={wid} bid={bid}
            onCidChange={(v) => { setCid(v); setWid(""); setBid(""); }}
            onWidChange={(v) => { setWid(v); setBid(""); }}
            onBidChange={setBid}
          />
        </WizardSection>
      ),
    },
    {
      title: "Title",
      canNext: !!title.trim(),
      blockerHint: "Title is required",
      body: (
        <WizardSection title="What is the case about?" hint="A short title that another officer would recognise; a longer summary for context.">
          <WizardField label="Title">
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Suspected defection — branch chair" />
          </WizardField>
          <WizardField label="Summary">
            <textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={5} placeholder="What you know so far. No speculation." />
          </WizardField>
        </WizardSection>
      ),
    },
    {
      title: "Subject",
      canNext: true,
      body: (
        <WizardSection title="Subject (optional)" hint="If the case is about a specific person, capture their identifiers. PII is masked from anyone without pii.read.">
          <WizardField label="Full name">
            <input value={subjectName} onChange={(e) => setSubjectName(e.target.value)} placeholder="e.g. Chibwe Sakala" />
          </WizardField>
          <WizardField label="National ID">
            <input value={subjectIdNumber} onChange={(e) => setSubjectIdNumber(e.target.value)} placeholder="######/##/#" />
          </WizardField>
          <WizardField label="Phone">
            <input value={subjectPhone} onChange={(e) => setSubjectPhone(e.target.value)} placeholder="+260…" />
          </WizardField>
        </WizardSection>
      ),
    },
    {
      title: "Threat",
      canNext: threatTo === "none" || (!!threatTarget.trim()),
      blockerHint: "If this is a threat, capture who's being threatened.",
      body: (
        <ThreatStepBody
          threatTo={threatTo}
          threatTarget={threatTarget}
          threatDetails={threatDetails}
          onThreatTo={setThreatTo}
          onThreatTarget={setThreatTarget}
          onThreatDetails={setThreatDetails}
        />
      ),
    },
    {
      title: "Classification",
      canNext: true,
      body: (
        <WizardSection title="How sensitive is this case?" hint="Restricted/Secret cases are hidden from anyone without case.read_classified.">
          <WizardField label="Classification">
            <select value={classification} onChange={(e) => setClassification(e.target.value as Classification)}>
              <option value="unclassified">Unclassified</option>
              <option value="restricted">Restricted</option>
              <option value="secret">Secret</option>
            </select>
          </WizardField>
        </WizardSection>
      ),
    },
    {
      title: "Review",
      canNext: !!title.trim() && !!bid,
      body: (
        <WizardSection title="Review and create" hint="The case opens in 'Open' status; you can move it through the workflow afterwards.">
          <dl className="kv">
            <dt>Title</dt><dd>{title}</dd>
            <dt>Classification</dt><dd>{classification}</dd>
            <dt>Branch</dt><dd>{branchesForWid.find((b) => b.id === bid)?.name ?? bid}</dd>
            <dt>Subject</dt><dd>{subjectName || "—"}</dd>
            <dt>Summary</dt><dd style={{ whiteSpace: "pre-wrap" }}>{summary || "—"}</dd>
          </dl>
        </WizardSection>
      ),
    },
  ];

  return (
    <div>
      {error && <div className="error" style={{ marginBottom: 8 }}>{error}</div>}
      <Wizard
        title="New case"
        steps={steps}
        busy={create.isPending}
        submitLabel="Create case"
        onSubmit={submit}
        onCancel={() => nav("..")}
      />
    </div>
  );
}

function CaseEditForm({
  initial, onCancel, onSave, saving,
}: {
  initial: Case;
  onCancel: () => void;
  onSave: (patch: Partial<{ title: string; summary: string; classification: Classification; subjectName: string; subjectIdNumber: string; subjectPhone: string }>) => void;
  saving: boolean;
}) {
  const [title, setTitle] = useState(initial.title);
  const [summary, setSummary] = useState(initial.summary || "");
  const [classification, setClassification] = useState<Classification>(initial.classification);
  const [subjectName, setSubjectName] = useState(initial.subjectName ?? "");
  const [subjectIdNumber, setSubjectIdNumber] = useState(initial.subjectIdNumber ?? "");
  const [subjectPhone, setSubjectPhone] = useState(initial.subjectPhone ?? "");

  function submit(e: FormEvent) {
    e.preventDefault();
    onSave({
      title,
      summary,
      classification,
      subjectName: subjectName || "",
      subjectIdNumber: subjectIdNumber || "",
      subjectPhone: subjectPhone || "",
    });
  }

  return (
    <form className="create-role" onSubmit={submit}>
      <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
        Edit the case details. Changing the classification is recorded — raising it requires <code>case.read_classified</code>.
      </div>
      <div className="row-3">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" required />
        <select value={classification} onChange={(e) => setClassification(e.target.value as Classification)}>
          <option value="unclassified">Unclassified</option>
          <option value="restricted">Restricted</option>
          <option value="secret">Secret</option>
        </select>
        <span />
      </div>
      <textarea
        value={summary}
        onChange={(e) => setSummary(e.target.value)}
        placeholder="Summary"
        rows={4}
        style={{ marginTop: 8, width: "100%" }}
      />
      <fieldset style={{ marginTop: 12, padding: 12, border: "1px solid var(--border)", borderRadius: 6 }}>
        <legend className="muted" style={{ padding: "0 6px" }}>Subject</legend>
        <div className="row-3">
          <input value={subjectName} onChange={(e) => setSubjectName(e.target.value)} placeholder="Full name" />
          <input value={subjectIdNumber} onChange={(e) => setSubjectIdNumber(e.target.value)} placeholder="ID number" />
          <input value={subjectPhone} onChange={(e) => setSubjectPhone(e.target.value)} placeholder="Phone" />
        </div>
      </fieldset>
      <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
        <button type="submit" disabled={saving}>{saving ? "Saving…" : "Save changes"}</button>
        <button type="button" className="ghost" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </form>
  );
}

function ClassificationBadge({ level }: { level: Classification }) {
  const color = level === "secret" ? "#b14444" : level === "restricted" ? "#a87b1c" : "#3a8053";
  return (
    <span style={{
      marginLeft: 8,
      fontSize: 11,
      padding: "2px 6px",
      background: color,
      color: "white",
      borderRadius: 4,
      textTransform: "uppercase",
      letterSpacing: 0.5,
    }}>{level}</span>
  );
}

function StatusBadge({ status }: { status: CaseStatus }) {
  return (
    <span style={{
      marginLeft: 6,
      fontSize: 11,
      padding: "2px 6px",
      background: "var(--border)",
      color: "var(--text)",
      borderRadius: 4,
    }}>{status.replace("_", " ")}</span>
  );
}
